import type {EditorAdapter,EditorPort,EditorState,EditorTransaction} from './EditorPort'

/** One owner for every asynchronous selection, inference, mutation and save.
 * Cancellation invalidates a lease immediately, but does not recycle an
 * uncorrelated native reply while its old operation is still draining. */
export function createEditorSession(adapter:EditorAdapter):EditorPort {
 let closed=false
 let active:{owner:string;operation:string;cancelled:boolean;controller:AbortController}|null=null
 const listeners=new Set<(state:EditorState)=>void>()
 const state=():EditorState=>({phase:closed?'closed':active?'busy':adapter.blocked()?'blocked':adapter.loaded()?'ready':'opening',operation:active?.operation||null,capabilities:adapter.capabilities()})
 const emit=()=>{for(const listener of listeners)listener(state())}
 const unsubscribe=adapter.subscribe(emit)
 async function run<T>(owner:string,operation:string,work:(tx:EditorTransaction)=>Promise<T>,isCurrent:()=>boolean=()=>true):Promise<T>{
  if(closed)throw Error('The editor was closed.')
  if(active||adapter.blocked()&&operation!=='save')throw Error('Another editor request is unfinished. Wait for it, or reopen the file after a selection timeout.')
  if(!adapter.loaded())throw Error('The editor is still opening. Try again when it is ready.')
  const lease={owner,operation,cancelled:false,controller:new AbortController()};active=lease;emit()
  const current=()=>!closed&&active===lease&&!lease.cancelled&&isCurrent()
  const check=()=>{if(!current())throw Error('Editor request cancelled; no late result can be applied.')}
  const after=async<T>(promise:Promise<T>)=>{const result=await promise;check();return result}
  const tx:EditorTransaction={current,signal:lease.controller.signal,
   captureSelection:()=>{check();return after(adapter.captureSelection())},
   copyText:()=>{check();return after(adapter.copyText())},
   focus:selection=>{check();return after(adapter.focus(selection))},
   paste:data=>{check();return after(Promise.resolve(adapter.paste(data)))},
   insertInline:(id,text)=>{check();return after(adapter.insertInline(id,text))},
   save:()=>{check();return after(adapter.save(current))},
   command:command=>{check();return after(adapter.command(command))},
  }
  try{check();const result=await work(tx);check();return result}
  finally{if(active===lease){active=null;emit()}}
 }
 return {state,idle:()=>!closed&&!active&&!adapter.blocked()&&adapter.loaded(),
  subscribe(listener){listeners.add(listener);listener(state());return()=>{listeners.delete(listener)}},
  onInline:adapter.onInline,run,
  cancel(owner){if(active?.owner===owner){active.cancelled=true;active.controller.abort();emit()}},
  save:()=>run('workspace','save',tx=>tx.save()),
  command:command=>run('commands',command,tx=>tx.command(command)),
  configureInline:adapter.configureInline,inlineResult:adapter.inlineResult,
  openInline:adapter.openInline,closeInline:adapter.closeInline,hideSidebar:adapter.hideSidebar,
  dispose(){if(closed)return;closed=true;if(active){active.cancelled=true;active.controller.abort()}unsubscribe();adapter.dispose();emit();listeners.clear()},
 }
}
